import { Flex, Grid, Stack, Switch, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useEffect, useMemo, useState } from 'react';

import { Declaration, DeclarationStatus } from '@e-origin/shared';

import { failedIcon, notSentIcon, releasedIcon } from '@assets';
import { EucdmCode, HideElementFor, InputImage, MultiLangCodes, TextInput } from '@components';
import { useCheckExporterEori } from '@hooks/declaration';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { isHighValueDeclaration } from '@utils';
import { SellerForm } from '../SellerForm';
import { DeclarationStakeholdersFormValues } from './validateDeclarationStakeholdersForm';

interface StakeholderExporterSectionProps {
  form: UseFormReturnType<DeclarationStakeholdersFormValues>;
  editDisabled?: boolean;
  declaration: Declaration;
  isHighValue: boolean;
  declarationGroup: string;
}

export const StakeholderExporterSection: React.FC<StakeholderExporterSectionProps> = ({ form, editDisabled, declaration, isHighValue, declarationGroup }) => {
  const checkExporterEoriMutation = useCheckExporterEori();
  const codelistSystems = useCodeListSystem({
    group: declarationGroup,
    isExport: declaration?.generalInfo?.isExport,
  });

  const isExport = !!declaration?.generalInfo?.isExport;

  const canVerifyDeclaration = useMemo(
    () => [DeclarationStatus.ANALYSIS, DeclarationStatus.NOT_SENT, DeclarationStatus.REJECTED].includes(declaration.generalInfo.status),
    [declaration.generalInfo.status],
  );

  const verifyExporterEori = async () => {
    if (!canVerifyDeclaration) return;

    checkExporterEoriMutation.mutate({
      _id: declaration._id,
      lrn: declaration.messageInfo.LRN,
      eori: form.values.stakeholders.exporter.identificationNumber,
    });
  };

  const [exporterEoriStatus, setExporterEoriStatus] = useState({ value: undefined, icon: notSentIcon });

  useEffect(() => {
    if (declaration.stakeholders.exporter?.evalEORI?.eoriOk === undefined) {
      setExporterEoriStatus({ value: undefined, icon: notSentIcon });
    } else if (declaration.stakeholders.exporter.evalEORI.eoriOk) {
      setExporterEoriStatus({ value: true, icon: releasedIcon });
    } else {
      setExporterEoriStatus({ value: false, icon: failedIcon });
    }
  }, [declaration]);

  return (
    <Stack>
      <Stack mb="xl">
        <Flex justify="space-between" align="center">
          <Title order={5} fw={600}>
            Exporter
          </Title>
          <Switch
            {...form.getInputProps('stakeholders.exporter.header')}
            checked={form.values.stakeholders.exporter.header}
            label="Header"
            disabled={editDisabled}
            size="lg"
          />
        </Flex>

        <Grid>
          <Grid.Col span={6}>
            <EucdmCode path="stakeholders.exporter.name">
              <TextInput {...form.getInputProps('stakeholders.exporter.name')} label="Name" disabled={editDisabled} />
            </EucdmCode>
          </Grid.Col>
          <HideElementFor systems={['DECO', 'IDMS_LV']} declarationGroup={declarationGroup}>
            <Grid.Col span={6}>
              <EucdmCode path="stakeholders.exporter.identificationNumber">
                <TextInput
                  {...form.getInputProps('stakeholders.exporter.identificationNumber')}
                  label="Identification number"
                  rightSection={
                    isHighValueDeclaration(declaration.generalInfo.group) && isExport ? (
                      <InputImage
                        picture={exporterEoriStatus.icon}
                        onPictureClick={verifyExporterEori}
                        disabled={!canVerifyDeclaration}
                        tooltip="Validation of EORI status"
                      />
                    ) : null
                  }
                  disabled={editDisabled}
                />
              </EucdmCode>
            </Grid.Col>
          </HideElementFor>
        </Grid>

        <Grid>
          <Grid.Col span={3}>
            <EucdmCode path="stakeholders.exporter.address.street">
              <TextInput {...form.getInputProps('stakeholders.exporter.address.street')} label="Street and number" disabled={editDisabled} />
            </EucdmCode>
          </Grid.Col>
          <Grid.Col span={3}>
            <EucdmCode path="stakeholders.exporter.address.postcode">
              <TextInput {...form.getInputProps('stakeholders.exporter.address.postcode')} label="Postcode" disabled={editDisabled} />
            </EucdmCode>
          </Grid.Col>
          <Grid.Col span={3}>
            <EucdmCode path="stakeholders.exporter.address.city">
              <TextInput {...form.getInputProps('stakeholders.exporter.address.city')} label="City" disabled={editDisabled} />
            </EucdmCode>
          </Grid.Col>
          <Grid.Col span={3}>
            <EucdmCode path="stakeholders.exporter.address.country">
              <MultiLangCodes
                {...form.getInputProps('stakeholders.exporter.address.country')}
                label="Country"
                code="CL199"
                disabled={editDisabled}
                system={codelistSystems}
              />
            </EucdmCode>
          </Grid.Col>
        </Grid>
      </Stack>

      {isHighValue && <SellerForm form={form} editDisabled={editDisabled} declarationGroup={declarationGroup} isExport={declaration?.generalInfo?.isExport} />}
    </Stack>
  );
};
